/* [PATCH dobavnica-cmr-fe v2] CMR tovorni list v maski: podatki iz čl. 6 Konvencije CMR (poslovni_dok.prevoz).
   Prazna polja z oznako »privzeto« strežnik predizpolni ob izpisu in preverbi (cmr_kontekst.cmr_polja): prevzem = naslov podjetja
   + datum dobave, dostava = prejemnik blaga, oznake / tovorki / embalaža iz paketov, priložene listine = izvorni dokument.
   Obvezno (*): brez teh se CMR ne izda — seznam manjkajočih pride s strežnika (dok.cmr_manjka). */
import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, Search } from 'lucide-react';
import { PartnerSelector } from '../PartnerSelector';

export type Prevoz = Record<string, string>;

const inp = 'w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-70';
const lbl = 'block text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5';
const zv = <span className="text-rose-500">*</span>;

export function CmrPrevoz({ prevoz, onChange, readonly, companyId, manjka, privzeto }: {
  prevoz: Prevoz; onChange: (p: Prevoz) => void; readonly?: boolean; companyId: string; manjka?: string[] | null;
  privzeto: { dostava: string; prevzemDatum: string; oznake: string; tovorki: string; embalaza: string; krajIzdaje: string };
}) {
  const [iskanje, setIskanje] = useState(false);
  const v = (k: string) => prevoz[k] || '';
  const nastavi = (k: string, x: string) => onChange({ ...prevoz, [k]: x });
  const izberiPrevoznika = (p: any) => {
    if (!p) return;
    const naslov = [p.address || p.naslov, [p.postal_code || p.posta, p.city || p.kraj].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    onChange({ ...prevoz, prevoznik_partner_id: String(p.id || ''), prevoznik_naziv: p.name || p.naziv || '', prevoznik_naslov: naslov,
      prevoznik_drzava: (p.country || 'SI').toUpperCase().slice(0, 2), prevoznik_id_ddv: p.vat_number || '' });
    setIskanje(false);
  };
  const polje = (k: string, oznaka: React.ReactNode, ph = '', cls = '', tip = 'text') => (
    <div className={cls}><span className={lbl}>{oznaka}</span>
      <input type={tip} className={inp} value={v(k)} disabled={readonly} placeholder={ph} onChange={e => nastavi(k, e.target.value)} /></div>
  );
  return (
    <div className="rounded-xl border bg-white dark:bg-gray-900 dark:border-gray-700 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-sm font-semibold">CMR — podatki o prevozu</div>
        <span className="text-xs text-gray-500">Konvencija CMR, 6. člen · {zv} obvezno · prazno »privzeto« izpolni sistem</span>
      </div>
      {Array.isArray(manjka) && (manjka.length > 0
        ? <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-500/10 p-2 text-xs text-amber-900 dark:text-amber-200 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /><div><b>Za izdajo manjka:</b> {manjka.join(', ')}. <span className="text-amber-700">(stanje ob zadnjem shranjevanju)</span></div></div>
        : <div className="rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 p-2 text-xs text-emerald-800 dark:text-emerald-200 flex gap-2"><CheckCircle2 className="w-4 h-4 shrink-0" />Vsi obvezni podatki so izpolnjeni.</div>)}

      <div>
        <div className="flex items-center gap-2 mb-1"><span className="text-xs font-semibold text-gray-600 dark:text-gray-300">16 · Prevoznik {zv}</span>
          {!readonly && <button type="button" className="ml-auto inline-flex items-center gap-1 text-xs text-blue-600 hover:underline" onClick={() => setIskanje(x => !x)}><Search className="w-3.5 h-3.5" /> iz šifranta partnerjev</button>}</div>
        {iskanje && !readonly && (
          <div className="mb-2 border rounded-lg p-2 bg-gray-900 text-white border-gray-700">
            <PartnerSelector companyId={companyId} onSelect={izberiPrevoznika} maxBrezIskanja={5} />
            <div className="flex justify-end mt-1"><button className="text-xs text-gray-400 hover:underline" onClick={() => setIskanje(false)}>zapri</button></div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.4fr)_minmax(0,2fr)_4.5rem_minmax(0,1fr)] gap-2">
          {polje('prevoznik_naziv', <>Naziv {zv}</>)}
          {polje('prevoznik_naslov', <>Naslov, pošta, kraj {zv}</>)}
          <div><span className={lbl}>Država</span><input className={inp + ' uppercase'} maxLength={2} value={v('prevoznik_drzava')} disabled={readonly} placeholder="SI" onChange={e => nastavi('prevoznik_drzava', e.target.value.toUpperCase())} /></div>
          {polje('prevoznik_id_ddv', 'ID za DDV')}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
          {polje('registrska_vozilo', 'Registrska — vozilo (23)', 'npr. LJ AB-123')}
          {polje('registrska_prikolica', 'Registrska — priklopnik (23)')}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_10rem_minmax(0,2fr)] gap-2">
        {polje('kraj_prevzema', <>4 · Kraj prevzema {zv}</>, 'privzeto: naslov podjetja')}
        {polje('datum_prevzema', <>4 · Datum prevzema {zv}</>, privzeto.prevzemDatum, '', 'date')}
        {polje('kraj_dostave', <>3 · Kraj dostave {zv}</>, privzeto.dostava ? 'privzeto: ' + privzeto.dostava : 'naslov prejemnika blaga')}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.3fr)_7rem_minmax(0,1fr)_minmax(0,1.6fr)] gap-2">
        {polje('oznake', <>6 · Oznake in številke {zv}</>, privzeto.oznake ? 'privzeto: ' + privzeto.oznake : 'npr. brez oznak')}
        {polje('st_tovorkov', <>7 · Št. tovorkov {zv}</>, privzeto.tovorki ? 'privzeto: ' + privzeto.tovorki : '')}
        {polje('embalaza', <>8 · Način pakiranja {zv}</>, privzeto.embalaza ? 'privzeto: ' + privzeto.embalaza : 'npr. EUR-paleta')}
        {polje('prilozene_listine', '5 · Priložene listine', 'privzeto: izvorni dokument + specifikacija pakiranja')}
      </div>
      <div className="text-[11px] text-gray-500">9–12 (vrsta blaga, statistična številka, bruto teža, prostornina) se izpolnijo iz postavk in paketov — bruto teža je obvezna {zv}.</div>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-2">
        {polje('navodila', '13 · Navodila pošiljatelja (carina, druge formalnosti)')}
        {polje('adr', 'ADR — razred, številka, črka', 'samo za nevarno blago')}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[14rem_minmax(0,1fr)_minmax(0,2fr)] gap-2">
        <div><span className={lbl}>14 · Plačilo prevoznine {zv}</span>
          <select className={inp} value={v('placilo_prevoza')} disabled={readonly} onChange={e => nastavi('placilo_prevoza', e.target.value)}>
            <option value="">— izberi —</option><option value="franko">franko (plača pošiljatelj)</option><option value="nefranko">nefranko (plača prejemnik)</option>
          </select></div>
        {polje('povzetje', '15 · Povzetje (znesek, valuta)')}
        {polje('posebni_dogovori', '19 · Posebni dogovori', 'npr. prekladanje ni dovoljeno, rok prevoza, napovedana vrednost')}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {polje('kraj_izdaje', <>21 · Izdano v {zv}</>, privzeto.krajIzdaje ? 'privzeto: ' + privzeto.krajIzdaje : '')}
        {polje('datum_izdaje', <>21 · Datum izdaje</>, '', '', 'date')}
      </div>
      <div className="text-[11px] text-gray-500">Polja 17 (naslednji prevozniki), 18 (pridržki prevoznika), 20 (stroški) in podpisi 22–24 se izpolnijo ročno na natisnjenem tovornem listu. Izpis: 3 izvirniki (pošiljatelj, prejemnik, prevoznik).</div>
    </div>
  );
}
